import { useCallback, useEffect, useState, useMemo } from "react";
import Header from "./Header/Header";
import SymbolCard from "./SymbolCard";
import toast from "react-hot-toast";
import useSize from "../../../Utils/useSize/useSize";
import Backend from "../../../Backend/Backend";
import Loading from "../../../Utils/Loading/Loading";
import NotFound from "../../../Utils/NotFound/NotFound";
import Menu from "./Menu/Menu";

const backend = await new Backend().init();

const FILTER_MAP = {
  0: "nse",
  1: "nse_cash",
  2: "fo",
  3: "mcx",
  4: "mcx_options",
  5: "comex_future",
  6: "forex_future",
  7: "crypto_future",
  8: "us_stock",
  9: "sgx",
  10: "index",
  11: "dgcx",
  12: "ncdex",
};

export default function SymbolQty({ user_id }) {
  const windowSize = useSize();

  // 1. Core Data State
  const [isLoading, setIsLoading] = useState(false);
  const [symbolList, setSymbolList] = useState([]); // The Master List
  const [access, setAccess] = useState({
    nse: 0,
    nse_cash: 0,
    options: 0,
    mcx: 0,
    mcx_options: 0,
    comex: 0,
    forex: 0,
    crypto: 0,
    us_stock: 0,
    sgx: 0,
    index: 0,
    dgcx: 0,
    ncdex: 0,
  });

  // 2. Interaction State
  const [currentFilter, setCurrentFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  /** ------------------- LOAD DATA ------------------- **/
  const loadSymbolQty = useCallback(async () => {
    setIsLoading(true);

    const data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      token: localStorage.getItem("token"),
      username: localStorage.getItem("username"),
      user_id: user_id,
    };

    try {
      const r = await backend.symbol_qty_list(data);
      if (r.error === "False") {
        setSymbolList(r.symbol || []);
        setAccess({
          nse: r.nse,
          nse_cash: r.nse_cash,
          mcx: r.mcx,
          options: r.options,
          mcx_options: r.mcx_options,
          comex: r.comex,
          crypto: r.crypto,
          forex: r.forex,
          us_stock: r.us_stock,
          sgx: r.sgx,
          index: r.index,
          dgcx: r.dgcx,
          ncdex: r.ncdex,
        });
      } else {
        if (r.logout === 1) {
          window.LogOut?.();
        } else {
          toast.error(r.message);
        }
      }
    } catch (error) {
      console.error("Failed to load Symbol Qty:", error);
      toast.error("Failed to fetch symbol qty data.");
    } finally {
      setIsLoading(false);
    }
  }, [user_id]);

  useEffect(() => {
    loadSymbolQty();
  }, [loadSymbolQty]);

  /** ------------------- DERIVED FILTERING ------------------- **/
  // This replaces your old filterSymbols and search functions.
  // It computes the list to display every time symbolList, filter, or search changes.
  const displaySymbols = useMemo(() => {
    return symbolList.filter((item) => {
      // Step A: Category Filter
      const categoryMatch =
        currentFilter === null || item.type === FILTER_MAP[currentFilter];

      // Step B: Search Query
      const searchMatch =
        searchQuery === "" ||
        item.symbol_display?.toLowerCase().includes(searchQuery.toLowerCase());

      return categoryMatch && searchMatch;
    });
  }, [symbolList, currentFilter, searchQuery]);

  /** ------------------- HANDLERS ------------------- **/
  const handleFilterChange = useCallback((typeIndex) => {
    setCurrentFilter(typeIndex);
  }, []);

  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query.trim());
  }, []);

  return (
    <div
      className="flex flex-col"
      style={{
        width: windowSize[0] - 100,
        height: windowSize[1] - 170,
      }}
      key={currentFilter}
    >
      <Header
        filter={handleFilterChange}
        {...access}
        search={handleSearchChange}
      />

      <Menu />

      {isLoading ? (
        <Loading height={windowSize[1] - 270} />
      ) : displaySymbols.length === 0 ? (
        <NotFound
          title={symbolList.length === 0 ? "No Data" : "No results found"}
          subtext="Try changing your filters or search query."
          height={windowSize[1] - 270}
        />
      ) : (
        <div
          className="hide-scrollbar"
          style={{
            height: windowSize[1] - 270,
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {displaySymbols.map((item) => (
            <SymbolCard
              key={item.id}
              item={item}
              user_id={user_id}
              loadSymbolQty={loadSymbolQty}
            />
          ))}
        </div>
      )}
    </div>
  );
}
